import React from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ForwardRoundedIcon from '@mui/icons-material/ForwardRounded'
import { renderBodyForType } from '../../panels/patientHistory/renderBodyForType'
import { advancedColors } from '../../theme'
import ActivityContextBody from './ActivityContextBody'
import { ForwardedFeedItem, forwardedItemPreviewLabel } from './types'

const Header = ({ label, summary }: { label: string; summary?: string | null }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <ForwardRoundedIcon fontSize="small" sx={{ color: advancedColors.inkFaint, flexShrink: 0 }} />
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: advancedColors.ink,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    {summary && (
      <Typography variant="caption" sx={{ color: advancedColors.inkMuted, flexShrink: 0 }}>
        {summary}
      </Typography>
    )}
  </Box>
)

const cardSx = {
  border: `1px solid ${advancedColors.line}`,
  borderRadius: 2,
  backgroundColor: advancedColors.surface,
  p: 1,
} as const

const CollapsibleCard = ({
  label,
  summary,
  children,
}: {
  label: string
  summary?: string | null
  children: React.ReactNode
}) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{
      border: `1px solid ${advancedColors.line}`,
      borderRadius: 2,
      backgroundColor: advancedColors.surface,
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreRoundedIcon />}
      sx={{
        minHeight: 0,
        '&.Mui-expanded': { minHeight: 0 },
        '& .MuiAccordionSummary-content': { margin: '8px 0' },
        '& .MuiAccordionSummary-content.Mui-expanded': { margin: '8px 0' },
      }}
    >
      <Header label={label} summary={summary} />
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
)

const ForwardedItemCard = ({ item }: { item: ForwardedFeedItem }) => {
  if (item.kind === 'chat') {
    return (
      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <ForwardRoundedIcon fontSize="small" sx={{ color: advancedColors.inkFaint, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: advancedColors.inkMuted }}>
            {forwardedItemPreviewLabel(item)}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 0.5, color: advancedColors.ink, overflowWrap: 'break-word' }}>
          {item.label}
        </Typography>
      </Box>
    )
  }

  if (item.kind === 'activity') {
    // Calls, lab orders and completions carry neither, so a collapsible body would reveal nothing.
    if (!item.context && !item.detail) {
      return (
        <Box sx={cardSx}>
          <Header label={item.label} />
        </Box>
      )
    }

    return (
      <CollapsibleCard label={item.label} summary={item.context?.summary}>
        <ActivityContextBody label={item.label} detail={item.detail} context={item.context} />
      </CollapsibleCard>
    )
  }

  return <CollapsibleCard label={item.label}>{renderBodyForType(item.entry)}</CollapsibleCard>
}

export default ForwardedItemCard
